import React from 'react';
import { Helmet } from 'react-helmet-async';

export interface FAQItem {
  question: string;
  answer: string;
}

interface MetaProps {
  title: string;
  desc: string;
  tags?: string;
  canonical?: string;
  url?: string;
  img?: string;
  type?: string;
  faqItems?: FAQItem[];
  schema?: Record<string, unknown> | Array<Record<string, unknown>>;
  noindex?: boolean;
}

const DEFAULT_IMAGE = "https://calculatorsheet.com/placeholder.svg";
const DEFAULT_SITE_URL = "https://calculatorsheet.com";
const SITE_NAME = "CalculatorSheet";

const Meta: React.FC<MetaProps> = ({
  title,
  desc,
  tags = "calculator, online calculator, free calculator, finance calculator, utility calculator, math calculator, calculatorsheet, utiloom",
  canonical,
  url,
  img = DEFAULT_IMAGE,
  type = "website",
  faqItems,
  schema,
  noindex = false,
}) => {
  const currentUrl = url || canonical || DEFAULT_SITE_URL;
  const canonicalUrl = canonical || currentUrl;

  // Format full title if not already branded
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

  // Build Graph Schemas
  const graphEntities: Array<Record<string, unknown>> = [
    {
      "@type": "Organization",
      "@id": "https://utiloom.in/#organization",
      "name": "Utiloom",
      "url": "https://utiloom.in",
      "logo": "https://calculatorsheet.com/favicon.ico",
      "sameAs": [
        "https://utiloom.in",
        "https://play.google.com/store/apps/details?id=com.utiloom.calcnote"
      ]
    },
    {
      "@type": "WebSite",
      "@id": "https://calculatorsheet.com/#website",
      "url": "https://calculatorsheet.com",
      "name": "CalculatorSheet",
      "description": "Free universal smart calculators for EMI, SIP, FD, Compound Interest, GST, Mileage, Age, BMI and more.",
      "publisher": {
        "@id": "https://utiloom.in/#organization"
      },
      "inLanguage": "en-US"
    },
    {
      "@type": "WebApplication",
      "@id": `${canonicalUrl}#webapp`,
      "url": canonicalUrl,
      "name": fullTitle,
      "description": desc,
      "applicationCategory": "UtilityApplication",
      "operatingSystem": "All",
      "browserRequirements": "Requires JavaScript. Requires HTML5.",
      "featureList": [
        "Interactive calculation sliders & inputs",
        "Instant amortization & compounding schedules",
        "Visual charts and breakdowns",
        "Mobile-optimized responsive design",
        "Privacy-first client-side computation"
      ],
      "creator": {
        "@id": "https://utiloom.in/#organization"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }
  ];

  // Add FAQPage Schema if FAQ items exist (Crucial for Google Rich Snippets)
  if (faqItems && faqItems.length > 0) {
    graphEntities.push({
      "@type": "FAQPage",
      "@id": `${canonicalUrl}#faq`,
      "mainEntity": faqItems.map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer,
        },
      })),
    });
  }

  const defaultSchema = {
    "@context": "https://schema.org",
    "@graph": graphEntities,
  };

  const activeSchema = schema || defaultSchema;

  return (
    <Helmet>
      {/* Primary HTML Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={desc} />
      <meta name="keywords" content={tags} />
      <meta name="author" content="Utiloom (https://utiloom.in)" />
      <meta name="publisher" content="Utiloom" />
      <meta name="creator" content="Utiloom" />

      {/* Standard Canonical Link Tag */}
      <link rel="canonical" href={canonicalUrl} />
      {/* LLM & Agentic Search Index Link */}
      <link rel="alternate" type="text/markdown" href="https://calculatorsheet.com/llms.txt" title="LLM Context Reference" />

      {/* Robots */}
      <meta 
        name="robots" 
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} 
      />

      {/* Google / Search Engine ItemProp Tags */}
      <meta itemProp="name" content={fullTitle} />
      <meta itemProp="description" content={desc} />
      <meta itemProp="image" content={img} />

      {/* Open Graph / Facebook Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* Google AdSense Script */}
      <script
        async
        src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1495327473535385"
        crossOrigin="anonymous"
      />

      {/* Schema.org JSON-LD Structured Data (with FAQPage & WebApplication) */}
      <script type="application/ld+json">
        {JSON.stringify(activeSchema)}
      </script>
    </Helmet>
  );
};

export default Meta;